import React, { ReactNode, RefObject, useState, useEffect, useCallback } from 'react';
import PanelDismiss from './panel-dismiss';
import { useClickOutside } from '../hooks/useClickOutside';
import { useIsMobile } from '../hooks/useIsMobile';

interface Props {
  children: ReactNode;
  className: string;
  expanded: boolean;
  onChange: () => void;
  handle: string;
  triggerRef?: RefObject<HTMLElement | null>;
  dynamicPosition?: boolean;
}

const SubMenuWrapper: React.FC<Props> = ({
  children, className, expanded, onChange, handle, triggerRef, dynamicPosition = false
}) => {
  const isMobile = useIsMobile();
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const menuRef = useClickOutside<HTMLDivElement>(
    expanded && !isMobile,
    onChange,
    triggerRef ? [triggerRef] : []
  );

  // Calculate position based on trigger element
  const updatePosition = useCallback(() => {
    if (!dynamicPosition || !triggerRef?.current || isMobile) {
      setPosition(null);
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const menuWidth = 220;
    const viewportWidth = window.innerWidth;

    // Position menu below the trigger, right-aligned to trigger
    let left = triggerRect.right - menuWidth;
    // Keep menu within viewport
    if (left < 8) left = 8;
    if (left + menuWidth > viewportWidth - 8) left = viewportWidth - menuWidth - 8;

    setPosition({
      top: triggerRect.bottom + 8,
      left,
    });
  }, [dynamicPosition, triggerRef, isMobile]);

  useEffect(() => {
    if (expanded) {
      updatePosition();
    }
  }, [expanded, updatePosition]);

  // The menu is fixed where its trigger was when it opened. Opening a chat
  // message's menu also scrolls that message to the top of the thread, so the
  // menu was left hanging over empty space a few hundred pixels below it.
  // Follow the trigger while anything scrolls (capture: the thread is its own
  // scroll box, and scroll does not bubble) or the window resizes.
  useEffect(() => {
    if (!expanded || !dynamicPosition || isMobile) return;
    const follow = () => updatePosition();
    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);
    return () => {
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
  }, [expanded, dynamicPosition, isMobile, updatePosition]);

  if (isMobile) {
    return (
      <>
        {expanded && (
          <div className="submenu-backdrop" onClick={onChange} />
        )}
        <PanelDismiss
          className={className}
          onChange={onChange}
          handle={handle}
          expanded={expanded}
        >
          {children}
        </PanelDismiss>
      </>
    );
  }

  // Desktop: simple dropdown with optional dynamic positioning
  const dynamicStyles: React.CSSProperties = {
    opacity: expanded ? 1 : 0,
    pointerEvents: expanded ? 'auto' : 'none',
    height: expanded ? 'auto' : 0,
  };

  if (dynamicPosition && position) {
    dynamicStyles.position = 'fixed';
    dynamicStyles.top = position.top;
    dynamicStyles.left = position.left;
    dynamicStyles.margin = 0;
    dynamicStyles.zIndex = 100;
    dynamicStyles.width = 220;
  }

  return (
    <div
      ref={menuRef}
      className={`${className} ${expanded ? 'desktop-expanded' : ''} ${dynamicPosition && position ? 'dynamic-positioned' : ''}`}
      style={dynamicStyles}
    >
      {children}
    </div>
  );
};

export default SubMenuWrapper;
